import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Moon, Eye, Volume2, Thermometer, Droplets, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SleepSanctuaryGuide() {
  const navigate = useNavigate();

  const elements = [
    { 
      element: "Darkness", 
      icon: Eye, 
      importance: "Light suppresses melatonin and disrupts sleep cycles",
      solutions: ["Blackout curtains or blinds", "Sleep mask", "Cover LED lights with tape", "Use dim red light if night light needed"],
      budget: "$10-50"
    },
    { 
      element: "Quiet", 
      icon: Volume2, 
      importance: "Noise disrupts sleep even if you don't wake up fully",
      solutions: ["White noise machine or fan", "Earplugs", "Heavy curtains (also dampen sound)", "Address snoring if partner has it"],
      budget: "$15-40"
    },
    { 
      element: "Cool Temperature", 
      icon: Thermometer, 
      importance: "Your body needs to cool down to initiate sleep",
      solutions: ["Set thermostat to 65-68°F", "Use a fan", "Cotton/bamboo breathable sheets", "Consider a cooling pillow"],
      budget: "$0-100"
    },
    { 
      element: "Scent", 
      icon: Droplets, 
      importance: "Lavender has proven calming effects on the nervous system",
      solutions: ["Lavender pillow spray", "Essential oil diffuser", "Fresh sheets weekly", "Remove any unpleasant odors"],
      budget: "$10-30"
    }
  ];

  const quickWins = [
    "Make your bedroom ONLY for sleep and intimacy—no work, no TV",
    "Keep your bedroom tidy—clutter creates mental noise",
    "Remove or cover clocks so you don't watch time pass",
    "Use your bed for sleep only—don't lounge there during the day",
    "Keep bedroom door closed for better temperature control"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-semibold mb-4">
            Week 5 • Day 5 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Sleep Sanctuary Setup
          </h1>
          <p className="text-xl text-secondary-600">Optimize Your Bedroom for Perfect Sleep</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-slate-700 to-slate-800 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Moon className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Today's Focus</h3>
              <p className="text-slate-300 text-lg">
                Make your bedroom darker, quieter, and cooler. These three elements have the biggest impact 
                on sleep quality. Add a lavender pillow spray for the finishing touch.
              </p>
            </div>
          </div>
        </section>

        {/* Four Elements */}
        <section className="space-y-6 mb-6">
          {elements.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <item.icon className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-secondary-900">{item.element}</h3>
                  <p className="text-sm text-slate-500">Budget: {item.budget}</p>
                </div>
              </div>
              <p className="text-secondary-600 mb-4">{item.importance}</p>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-slate-600 mb-2">Solutions:</p>
                <ul className="grid md:grid-cols-2 gap-2">
                  {item.solutions.map((sol, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <CheckCircle className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      {sol}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </section>

        {/* Quick Wins */}
        <section className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-8 border border-slate-200">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Quick Wins (Free!)</h2>
          <div className="space-y-3">
            {quickWins.map((tip, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg text-secondary-700">
                <CheckCircle className="w-5 h-5 text-slate-500 flex-shrink-0" />
                {tip}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SleepSanctuaryGuide;
